import { requestAsync, httpMethods } from 'redux-query';
import cfg from 'cfg';
import _ from 'lodash';
import { compose } from 'redux';
import auth from 'dash-front/auth';
import { toIdMap, toIdArray } from 'dash-front/helpers';
import { UPDATE_DEFAULT } from 'dash-front/requery/actions';
import { STORE_KEYS, POST_STATUS } from 'dash-front/constants';

const POSTS_KEY = STORE_KEYS.POSTS;

const getTriggerType = post => {
    if (post && typeof post.triggerKey === 'string') {
        return _.head(post.triggerKey.split('.'));
    }
    return null;
};

const mapPostFromServerData = post =>
    _.extend({}, post, {
        triggerType: getTriggerType(post),
        publications: Array.isArray(post.publications) ? toIdArray(post.publications) : [],
        facts: toIdArray(post.facts),
        hasResolvedFacts: post.facts.some(f => f.message),
    });

const transformPosts = ({ posts }) => ({
    [POSTS_KEY]: compose(
        toIdMap,
        posts => posts.map(mapPostFromServerData),
    )(posts),
    publications: compose(
        toIdMap,
        posts => posts.reduce((acc, post) => acc.concat(post.publications), []),
    )(posts),
    facts: compose(
        toIdMap,
        posts => posts.reduce((acc, post) => acc.concat(post.facts), []),
    )(posts),
});

const transformSinglePost = ({ post }) => {
    return {
        [POSTS_KEY]: { [post._id]: mapPostFromServerData(post) },
        publications: Array.isArray(post.publications) ? toIdMap(post.publications) : {},
        facts: toIdMap(post.facts),
    };
};

const updatePosts = {
    [POSTS_KEY]: UPDATE_DEFAULT.MERGE,
    publications: UPDATE_DEFAULT.MERGE,
    facts: UPDATE_DEFAULT.MERGE,
};

const getFetchUrl = ({ campaignIds, status, limit, skip }) => {
    let statusQuery = '';
    if (typeof status === 'string') {
        statusQuery = `&status=${status}`;
    } else if (typeof status === 'object') {
        statusQuery = `&status=${JSON.stringify(status)}`;
    }
    return (
        cfg.campaign_manager.host +
        '/api/posts?access_token=' +
        auth.getAccessToken() +
        '&campaignIds=' +
        JSON.stringify(campaignIds) +
        '&limit=' +
        limit +
        '&skip=' +
        skip +
        statusQuery
    );
};

export const baseFetchPosts = postStatus => ({
    campaignId,
    campaignIds = [campaignId],
    limit = 10,
    skip = 0,
    force = true,
    queryKey,
}) =>
    requestAsync({
        url: getFetchUrl({ campaignIds, status: postStatus, limit, skip }),
        force,
        queryKey,
        transform: transformPosts,
        update: updatePosts,
    });

export const fetchNotPublishedPosts = baseFetchPosts(POST_STATUS.NOT_PUBLISHED);
export const fetchPublishedPosts = baseFetchPosts(POST_STATUS.PUBLISHED);

export const approvePost = postId => {
    const url = cfg.campaign_manager.host + '/api/posts/' + postId + '/approve?access_token=' + auth.getAccessToken();
    const config = {
        url,
        queryKey: 'posts/' + postId + '/approve',
        key: `POST:${POSTS_KEY}/${postId}/approve`,
        force: true,
        transform: ({ post }) => transformSinglePost(post),
        update: updatePosts,
        options: {
            method: httpMethods.POST,
        },
    };
    return requestAsync(config);
};

export const updatePostMessage = postId => message => {
    const url = cfg.campaign_manager.host + '/api/posts/' + postId + '?access_token=' + auth.getAccessToken();
    const config = {
        url,
        body: { message },
        queryKey: 'posts/' + postId + '/update-message',
        force: true,
        transform: transformSinglePost,
        update: updatePosts,
        options: {
            method: httpMethods.POST,
        },
    };
    return requestAsync(config);
};

export const updatePost = postId => updates => {
    const url = cfg.campaign_manager.host + '/api/posts/' + postId + '?access_token=' + auth.getAccessToken();
    const config = {
        url,
        body: updates,
        queryKey: 'posts/' + postId,
        force: true,
        transform: transformSinglePost,
        update: updatePosts,
        options: {
            method: httpMethods.PUT,
        },
    };
    return requestAsync(config);
};

export const rejectPost = postId => {
    const url = cfg.campaign_manager.host + '/api/posts/' + postId + '/reject?access_token=' + auth.getAccessToken();
    const config = {
        url,
        queryKey: 'posts/' + postId + '/reject',
        key: POSTS_KEY,
        force: true,
        transform: ({ post }) => transformSinglePost(post),
        update: updatePosts,
        options: {
            method: httpMethods.POST,
        },
    };
    return requestAsync(config);
};

export const deletePost = postId => {
    const url = cfg.campaign_manager.host + '/api/posts/' + postId + '/delete?access_token=' + auth.getAccessToken();
    const config = {
        url,
        queryKey: 'posts/' + postId + '/delete',
        force: true,
        transform: ({ post }) => transformSinglePost(post),
        update: updatePosts,
        options: {
            method: httpMethods.POST,
        },
    };
    return requestAsync(config);
};

export const retrySendPost = postId =>
    requestAsync({
        url:
            cfg.campaign_manager.host +
            '/api/posts/' +
            postId +
            '/reset-attempts?access_token=' +
            auth.getAccessToken(),
        force: true,
        options: {
            method: httpMethods.POST,
        },
        transform: transformSinglePost,
        update: updatePosts,
    });

export const manualCreatePost = mappedData => (dispatch, getState) => {
    const url = cfg.campaign_manager.host + '/api/posts?access_token=' + auth.getAccessToken();
    const state = getState();
    mappedData.presentation.params.username = state.user.username;
    mappedData.presentation.params.client = state.user.client;
    mappedData.presentation.params.theme = 'default';

    const config = {
        url,
        body: mappedData,
        force: true,
        queryKey: 'manual-create-post',
        update: {},
        transform: _.noop,
        options: {
            method: httpMethods.POST,
        },
    };
    return dispatch(requestAsync(config));
};



/** WEBPACK FOOTER **
 ** ../src/dash/front/campaign/actions/posts.js
 **/